/**
 * Welcome Animation Component
 *
 * Theatrical fly-system animation that plays when users first visit
 * the Stagecraft website. Simulates a counterweight fly system bringing
 * in a welcome backdrop.
 *
 * Features:
 * - Synchronized backdrop descent and sandbag rise
 * - Subtle shake effect during movement
 * - Bounce settle when backdrop arrives
 * - Sound effect during animation
 * - Click anywhere to dismiss (reverse animation)
 * - Persistent sandbag to re-trigger
 * - Mobile-responsive (backdrop only on mobile)
 * - Respects prefers-reduced-motion
 */

import React, { useEffect, useRef, useCallback } from "react";
import { useWelcomeAnimation } from "./useWelcomeAnimation";
import { Backdrop } from "./Backdrop";
import { Counterweight } from "./Counterweight";
import { ASSETS } from "./constants";
import { isCancelKey } from "@/core/keyboard/isCancelKey";
import { BLOCKING_SURFACE_ATTR } from "@/core/keyboard/isBlockingSurfaceOpen";
import styles from "./WelcomeAnimation.module.css";

export function WelcomeAnimation() {
  const {
    phase,
    isVisible,
    showPersistentSandbag,
    triggerAnimation,
    dismissAnimation,
  } = useWelcomeAnimation();

  const overlayRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle click/touch to dismiss
  const handleDismiss = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      dismissAnimation();
    },
    [dismissAnimation],
  );

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase === "rest") {
        if (e.key === "Enter" || e.key === " " || isCancelKey(e)) {
          e.preventDefault();
          dismissAnimation();
        }
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [phase, isVisible, dismissAnimation]);

  // Focus management
  useEffect(() => {
    if (isVisible && phase !== "idle") {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Focus the overlay
      overlayRef.current?.focus();
    } else if (!isVisible && previousFocusRef.current) {
      // Restore focus when closing
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isVisible, phase]);

  // No longer preventing body scroll since overlay is transparent

  return (
    <>
      {/* Main animation overlay */}
      {isVisible && (
        <div
          ref={overlayRef}
          className={styles.overlay}
          onClick={handleDismiss}
          onTouchEnd={handleDismiss}
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to Stagecraft"
          tabIndex={-1}
          // WR-249 (registrable B-8). This overlay is rendered APP-WIDE, as a
          // sibling of <Routes> (AppRouter.tsx), so it is present on the CAD
          // route and not only on marketing pages. While it is displayed it is
          // genuinely blocking -- WELCOME-DROP-ANIMATION-PRD.md:83 makes ANY
          // click dismiss it -- so suppressing the canvas keyboard here is
          // correct.
          //
          // ⛔ THE BOUNDARY, AND IT IS THE DANGEROUS DIRECTION. The same PRD
          // (:99-110, "Phase 7: Idle State") requires the post-dismissal state
          // to leave the "Page | Fully visible and interactive". This element
          // is gated on `isVisible`, so it UNMOUNTS at idle and the marker goes
          // with it. If it were ever left mounted-but-hidden instead, this
          // attribute would become a PERMANENT, APP-WIDE KEYBOARD KILL with
          // nothing visible on screen -- strictly worse than the bug WR-249
          // fixes. The gate drives both sides for exactly this reason.
          {...{ [BLOCKING_SURFACE_ATTR]: "" }}
        >
          {/* Screen reader content */}
          <div className={styles.srOnly}>
            <h2>Welcome to Stagecraft</h2>
            <p>
              You are accessing the Open Alpha version of Stagecraft, a
              collaborative CAD platform for live entertainment. We invite you
              to use it freely and share your feedback to help guide
              development.
            </p>
            <p>Press Enter, Space, or Escape to dismiss this message.</p>
          </div>

          {/* Backdrop (the drop) */}
          <Backdrop phase={phase} />

          {/* Counterweight (sandbag) - hidden on mobile via CSS */}
          <Counterweight phase={phase} />
        </div>
      )}

      {/* Persistent sandbag that stays after animation ends - click to replay */}
      {showPersistentSandbag && (
        <button
          className={styles.persistentCounterweight}
          onClick={triggerAnimation}
          aria-label="Replay welcome animation"
        >
          <img
            src={ASSETS.SANDBAG}
            alt=""
            role="presentation"
            draggable={false}
          />
        </button>
      )}
    </>
  );
}

export default WelcomeAnimation;
